import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";

const POWER_WORDS = new Set([
  "proven", "free", "ultimate", "essential", "complete", "powerful", "secret",
  "secrets", "easy", "simple", "fast", "quick", "best", "top", "new", "now",
  "how", "why", "what", "guide", "boost", "grow", "growth", "win", "winning",
  "mistakes", "avoid", "stop", "start", "instantly", "today", "step-by-step",
  "checklist", "framework", "strategy", "strategies", "tips", "tricks", "hacks",
  "real", "honest", "data-backed", "expert", "definitive", "practical",
]);

const COMMON_WORDS = new Set([
  "the", "a", "an", "and", "or", "but", "of", "to", "in", "on", "for", "with",
  "your", "you", "that", "this", "is", "are", "was", "were", "be", "by", "at",
  "as", "it", "its", "from", "can", "will", "how", "why", "what",
]);

interface Analysis {
  score: number;
  words: number;
  chars: number;
  hasNumber: boolean;
  powerWords: string[];
  uncommonRatio: number;
  startsWithHowOrNumber: boolean;
  sentiment: "positive" | "negative" | "neutral";
  lengthVerdict: string;
  suggestions: string[];
}

const POSITIVE = new Set(["best", "win", "winning", "boost", "grow", "growth", "proven", "easy", "love", "great", "top"]);
const NEGATIVE = new Set(["mistakes", "avoid", "stop", "worst", "fail", "failing", "never", "warning", "killing"]);

function analyze(headline: string): Analysis {
  const clean = headline.trim();
  const tokens = clean.toLowerCase().replace(/[^a-z0-9\s-]/g, " ").split(/\s+/).filter(Boolean);
  const words = tokens.length;
  const chars = clean.length;
  const hasNumber = /\d/.test(clean);
  const powerWords = tokens.filter((t) => POWER_WORDS.has(t));
  const uncommon = tokens.filter((t) => !COMMON_WORDS.has(t));
  const uncommonRatio = words ? uncommon.length / words : 0;
  const startsWithHowOrNumber = /^(how|why|what|\d)/i.test(clean);

  let sentiment: Analysis["sentiment"] = "neutral";
  if (tokens.some((t) => POSITIVE.has(t))) sentiment = "positive";
  if (tokens.some((t) => NEGATIVE.has(t))) sentiment = "negative";

  let score = 40;
  const suggestions: string[] = [];

  if (words >= 6 && words <= 12) score += 15;
  else if (words > 0) {
    suggestions.push(words < 6 ? "Aim for 6–12 words — short headlines get truncated and vague ones get skipped." : "Trim to under 12 words so it fits search results and social cards.");
  }

  if (chars > 0 && chars <= 60) score += 10;
  else if (chars > 60) suggestions.push("Keep it under 60 characters so Google doesn't cut it off.");

  if (hasNumber) score += 12;
  else suggestions.push("Add a number — list-style headlines consistently earn more clicks.");

  if (powerWords.length > 0) score += Math.min(12, powerWords.length * 6);
  else suggestions.push("Add one power word like proven, essential, or complete to raise emotional pull.");

  if (uncommonRatio >= 0.4) score += 6;
  else suggestions.push("Use more specific, uncommon words — generic headlines blend in.");

  if (startsWithHowOrNumber) score += 5;

  if (sentiment !== "neutral") score += 5;
  else suggestions.push("Lean slightly positive (best, boost) or negative (mistakes, avoid) — neutral headlines underperform.");

  if (suggestions.length === 0) suggestions.push("Strong headline. Test a variation with a different power word or number.");

  const lengthVerdict =
    chars === 0 ? "—" : chars <= 60 ? "Fits search results" : "Likely truncated in search";

  return {
    score: Math.min(100, Math.max(0, score)),
    words,
    chars,
    hasNumber,
    powerWords,
    uncommonRatio,
    startsWithHowOrNumber,
    sentiment,
    lengthVerdict,
    suggestions,
  };
}

function scoreColor(score: number) {
  if (score >= 75) return "text-emerald-600";
  if (score >= 55) return "text-amber-600";
  return "text-rose-600";
}

export const Route = createFileRoute("/tools/headline-analyzer")({
  head: () => ({
    links: [{ rel: "canonical", href: "https://mgcontentlabs.com/tools/headline-analyzer" }],
    meta: [
      { property: "og:url", content: "https://mgcontentlabs.com/tools/headline-analyzer" },
      { title: "Free SEO Title Checker & Headline Analyzer | MG Content Labs" },
      {
        name: "description",
        content:
          "Free SEO title checker: paste a headline and get an instant title score for length, power words, numbers and clarity. No signup, works for blogs and meta titles.",
      },
      { property: "og:title", content: "Free SEO Title Checker & Headline Analyzer" },
      {
        property: "og:description",
        content:
          "Paste a headline and get an instant title score for length, power words, numbers and clarity. Free SEO title checker, no signup.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "MG Content Labs Headline Analyzer",
          url: "https://mgcontentlabs.com/tools/headline-analyzer",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          creator: { "@type": "Organization", name: "MG Content Labs" },
        }),
      },
    ],
  }),
  component: HeadlineAnalyzer,
});

function HeadlineAnalyzer() {
  const [headline, setHeadline] = useState("");
  const result = useMemo(() => (headline.trim() ? analyze(headline) : null), [headline]);

  return (
    <>
      <SiteNav />
      <main className="bg-white">
        <section className="border-b border-brand-deep/5 bg-brand-muted/40 py-16">
          <div className="mx-auto max-w-3xl px-6 text-center">
            <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-brand-accent">
              Free Tool
            </p>
            <h1 className="text-4xl font-bold tracking-tight text-brand-deep md:text-5xl">
              Free SEO Title Checker &amp; Headline Analyzer
            </h1>
            <p className="mx-auto mt-4 max-w-xl text-brand-deep/70">
              Paste a blog title, meta title or headline and get an instant title score based on
              length, power words, numbers and emotional pull — the same checks we run on every
              article we ship.
            </p>
          </div>
        </section>

        <section className="py-14">
          <div className="mx-auto max-w-3xl px-6">
            <label htmlFor="headline-input" className="mb-2 block text-sm font-semibold text-brand-deep">
              Your headline
            </label>
            <input
              id="headline-input"
              type="text"
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
              placeholder="e.g. 7 Proven Ways to Boost Your Blog Traffic in 2026"
              className="w-full rounded-xl border border-brand-deep/15 bg-white px-4 py-3 text-lg text-brand-deep shadow-sm outline-none focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20"
            />

            {result && (
              <div className="mt-8 space-y-6">
                <div className="flex items-center gap-6 rounded-2xl border border-brand-deep/10 bg-brand-muted/30 p-6">
                  <div className={`text-6xl font-bold tabular-nums ${scoreColor(result.score)}`}>
                    {result.score}
                  </div>
                  <div className="text-sm text-brand-deep/70">
                    <p className="font-semibold text-brand-deep">
                      {result.score >= 75 ? "Strong headline" : result.score >= 55 ? "Decent — room to improve" : "Needs work"}
                    </p>
                    <p>{result.words} words · {result.chars} characters · {result.lengthVerdict}</p>
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <Check label="Includes a number" ok={result.hasNumber} />
                  <Check label="Uses power words" ok={result.powerWords.length > 0} detail={result.powerWords.join(", ") || undefined} />
                  <Check label="Specific, uncommon wording" ok={result.uncommonRatio >= 0.4} />
                  <Check label="Emotional angle" ok={result.sentiment !== "neutral"} detail={result.sentiment !== "neutral" ? result.sentiment : undefined} />
                </div>

                <div className="rounded-2xl border border-brand-deep/10 p-6">
                  <h2 className="mb-3 text-sm font-bold uppercase tracking-widest text-brand-deep">
                    Suggestions
                  </h2>
                  <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-brand-deep/75">
                    {result.suggestions.map((s) => (
                      <li key={s}>{s}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <div className="mt-14 rounded-2xl bg-brand-deep p-8 text-center">
              <h2 className="text-2xl font-bold text-white">
                Want headlines written for you?
              </h2>
              <p className="mx-auto mt-2 max-w-md text-sm text-white/70">
                Our team writes and optimizes articles end to end — titles, structure, schema, and
                distribution included.
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-3">
                <Link
                  to="/services"
                  className="rounded-full bg-brand-accent px-6 py-2.5 text-sm font-semibold text-white transition hover:opacity-90"
                >
                  Explore Our Services
                </Link>
                <Link
                  to="/pricing"
                  className="rounded-full border border-white/30 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-white/10"
                >
                  View Pricing Plans
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}

function Check({ label, ok, detail }: { label: string; ok: boolean; detail?: string }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-brand-deep/10 p-4">
      <span
        className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
          ok ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
        }`}
      >
        {ok ? "✓" : "✕"}
      </span>
      <div>
        <p className="text-sm font-semibold text-brand-deep">{label}</p>
        {detail && <p className="text-xs text-brand-deep/60">{detail}</p>}
      </div>
    </div>
  );
}
